import axios, { CancelTokenSource } from 'axios';
import { DropdownOption, mapToDropdownOptionArrayWithSep } from 'components/final-form/dropdownUtils';
import Input, { StyledInput } from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { FormApi } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { findMedicamentById } from 'services/medicamentServices';
import {
  addStockAdministration,
  editStockAdministration,
  searchStockAdministration
} from 'services/stockAdministrationServices';
import { getAllSuppliers } from 'services/suppliersService';
import { StockAdministrationSearchBy } from 'ts-types/api.enums';
import {
  StockAdministrationSearchRequest,
  UpsertMedicamentDto,
  UpsertStockAdministrationDto
} from 'ts-types/api.types';
import { toNumber } from 'utils/numberUtils';

const medStyle = {paddingBottom: "0.3rem"}
const medTitleStyle = {paddingBottom: "0.8rem"}

interface Props extends MessageSourceProps {
  stockAdministrationId?: number;
  stockAdministration?: UpsertStockAdministrationDto;
  medicamentId?: number;
  mandateId?: number;
  resourceId?: number;
  fastEntry: boolean;
  onSave: () => void;
  onCancel: () => void;
}

interface State {
  stockAdministrationId?: number;
  stockAdministration?: Partial<UpsertStockAdministrationDto>;
  medicamentId?: number;
  medicamentData?: Partial<UpsertMedicamentDto>;
  supplierOptions: Array<DropdownOption>;
  requiredDataLoaded: boolean;
  searchString?: string;
  fastEntryResponseMsg?: string;
  formKey: number;
  errorMessage?: string;
}


class UpsertStockAdministrationForm extends Component<Props, State> {

  form: FormApi | undefined;

  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  containerNode: React.RefObject<any> = React.createRef();

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      stockAdministration: {},
      supplierOptions: [],
      requiredDataLoaded: false,
      searchString: "",
      formKey: 1
    };

    this.loadRequiredData(props, this.cancelTokenSource);
  }

  componentDidMount() {
    this.containerNode.current.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    this.containerNode.current.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'S')) {
      if (this.form) {
        event.preventDefault();
        this.handleSubmit(this.form.getState().values);
      }
    }
  }

  loadRequiredData = async (props: Props, cancelTokenSource: CancelTokenSource) => {

    const {
      stockAdministrationId,
      stockAdministration,
      medicamentId,
      fastEntry
    } = props;

    try {

      let medicamentResponse = undefined;
      if (medicamentId) {
        medicamentResponse = await findMedicamentById(medicamentId, cancelTokenSource);
      }

      const suppliers = await getAllSuppliers(cancelTokenSource);
      const supplierOptions = mapToDropdownOptionArrayWithSep(
          suppliers, ["description"], "id");

      let stockAdminValues: Partial<UpsertStockAdministrationDto> = {}
      if (!fastEntry) {
        stockAdminValues = {...stockAdministration};
      }

      this.setState({
        stockAdministrationId: stockAdministrationId,
        stockAdministration: stockAdminValues,
        medicamentId,
        medicamentData: medicamentResponse,
        supplierOptions: supplierOptions
      });
    } catch (e) {
      this.handleError(e.response.data);
    } finally {
      this.setState({
        requiredDataLoaded: true
      });
    }
  }

  handleSubmit = async (values: Partial<UpsertStockAdministrationDto>) => {

    const {
      fastEntry,
      mandateId,
      resourceId,
      onSave
    } = this.props;

    const {
      stockAdministrationId,
      stockAdministration,
      medicamentId,
      medicamentData
    } = this.state;

    let saveValues: Partial<UpsertStockAdministrationDto> = {...values};

    const stockAdminValues = stockAdministration;
    if (medicamentId !== null && (stockAdminValues && stockAdminValues.medicamentId)) {
      saveValues.medicamentId = stockAdminValues.medicamentId;
    } else {
      saveValues.medicamentId = medicamentId;
    }
    saveValues.mandateId = mandateId;
    saveValues.resourceId = resourceId;

    const onSaveSuccess = async () => {

      if (fastEntry) {
        await this.searchStockAdministration(medicamentData ? medicamentData.eanCode! : "", true);
        const searchInput =
            document.getElementById("stockAdministrationSearchTerm") as HTMLInputElement | null;
        searchInput && searchInput.select();
        return;
      }

      onSave && onSave();
    };

    if (stockAdministrationId) {
      await editStockAdministration(stockAdministrationId, saveValues, this.cancelTokenSource)
          .then(onSaveSuccess)
          .catch((e: any) => this.handleError(e.response.data))
    } else {
      await addStockAdministration(saveValues, this.cancelTokenSource)
          .then(onSaveSuccess)
          .catch((e: any) => this.handleError(e.response.data))
    }

  };

  searchStockAdministration = async (searchText: string, forceReload = false) => {
    const {
      mandateId,
      resourceId,
      getMessage
    } = this.props;

    const {
      searchString,
      formKey
    } = this.state;

    if (searchText === "") {
      this.setState({
        stockAdministrationId: undefined,
        stockAdministration: {},
        medicamentId: undefined,
        medicamentData: undefined,
        fastEntryResponseMsg: undefined,
        searchString: "",
        formKey: formKey + 1
      });
    } else if (searchText !== searchString || forceReload) {

      this.setState({searchString: searchText});

      const request: Partial<StockAdministrationSearchRequest> = {
        mandateId: mandateId,
        resourceId: resourceId,
        searchBy: StockAdministrationSearchBy.ALL,
        stockAdministrationSearchTerm: searchText,
        fastEntry: true
      };

      try {
        const response = await searchStockAdministration(request, this.cancelTokenSource);

        let msg = "";
        let stockAdministrationId: number | undefined = undefined;
        let medicament = undefined;

        let stockAdminValues: Partial<UpsertStockAdministrationDto> = {};
        if (response) {
          if (response.length === 0) {
            msg = getMessage("stockAdministration.noMedicamentFound");
          } else if (response.length > 1) {
            msg = getMessage("stockAdministration.multipleMedicamentsFound");
          } else {
            msg = getMessage("stockAdministration.foundEntry");
            stockAdministrationId = response[0].id;
            stockAdminValues = response[0] as UpsertStockAdministrationDto;
            medicament = await findMedicamentById(response[0].medicamentId, this.cancelTokenSource);
          }
        }

        this.setState({
          stockAdministrationId: stockAdministrationId,
          stockAdministration: stockAdminValues,
          medicamentId: stockAdminValues.medicamentId,
          medicamentData: medicament,
          fastEntryResponseMsg: msg,
          formKey: formKey + 1
        });

      } catch (e) {
        this.handleError(e.response.data)
      }
    }
  };

  handleQuantityChange = (event: any, form: FormApi) => {

    const eventKey = event.key;

    if (eventKey === '+' || eventKey === '-') {
      event.preventDefault();

      const values = form.getState().values;

      const quantity = toNumber(values.quantity);

      let minimumQuantity = toNumber(values.minimumQuantity, 1);
      minimumQuantity = Math.max(1, Math.abs(minimumQuantity));

      if (eventKey === '-') {
        minimumQuantity = -minimumQuantity;
      }

      const newQuantity = Math.max(0, quantity + minimumQuantity);

      form.change("quantity", newQuantity);
    }
  }

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  getDbQuantity = () => {
    const {getMessage} = this.props;

    const {
      stockAdministrationId,
      stockAdministration,
      medicamentId,
      medicamentData
    } = this.state;

    let dbQuantity;
    if (stockAdministrationId) {
      dbQuantity = `${stockAdministration!.quantity}`;
    } else if (medicamentId || medicamentData) {
      dbQuantity = getMessage("stockAdministration.notManaged");
    }

    return dbQuantity;
  }

  getQuantityDifference = (valQuantity: string | undefined) => {
    const {stockAdministration} = this.state;
    const originalQuantity = toNumber(stockAdministration ? stockAdministration.quantity : undefined);
    const quantity = toNumber(valQuantity);
    const diff = quantity - originalQuantity;

    if (diff === 0) {
      return ""
    } else if (diff > 0) {
      return `(+${diff})`;
    }
    return `(${diff})`;
  }

  render(): React.ReactNode {

    const {stockAdministration} = this.state;
    return <div id="stock-admin-form-container" ref={this.containerNode}>
      <FinalForm key={`sa-${this.state.formKey}`}
                 onSubmit={(values) => this.handleSubmit(values)}
                 initialValues={stockAdministration}
                 subscription={{values: true, pristine: true, submitting: true}}
                 render={this.renderForm}
      />
    </div>;
  }

  renderForm = ({handleSubmit, submitting, form}: FormRenderProps): React.ReactNode => {

    const {fastEntry, onCancel, getMessage} = this.props;
    const {errorMessage, medicamentData} = this.state;

    this.form = form;

    const disableBtn = form.getState().pristine || submitting || !medicamentData;

    return (
        <>
          {fastEntry && this.renderSearchForm(form)}
          <form onSubmit={handleSubmit} style={{visibility: !medicamentData ? "hidden" : 'visible'}}>
            <FormContainer>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Column style={{width: "50%"}}>
                  {this.renderStockAdministrationSegment(form.getState().values)}
                </Grid.Column>
                <Grid.Column style={{width: "50%"}}>
                  {this.renderMedicamentSegment()}
                </Grid.Column>
              </PraxFormGrid>
              <FormButtonsRow>
                <Button compact type="submit" primary disabled={disableBtn}>{getMessage("button.save")}</Button>
                <Button compact type="button" disabled={submitting}
                        onClick={onCancel}>{getMessage("button.cancel")}</Button>
              </FormButtonsRow>
            </FormContainer>
            {/*<FormSpy subscription={{values: true}} onChange={this.handleFormChange}/>*/}
          </form>
        </>
    )
  }

  renderSearchForm = (form: FormApi): React.ReactNode => {
    const {getMessage} = this.props;
    const {fastEntryResponseMsg, searchString} = this.state;

    return (
        <PraxFormGrid>
          <Grid.Row style={{marginBottom: "1.5rem", marginTop: "0.5rem"}}>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={7}>
              <StyledInput
                  id="stockAdministrationSearchTerm"
                  type="input"
                  value={searchString}
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                      this.searchStockAdministration(evt.target.value)}
                  autoFocus
                  autoComplete="off"
                  onKeyDown={(event: any) => this.handleQuantityChange(event, form)}
                  style={{width: "-webkit-fill-available"}}
              />
            </Grid.Column>
            <Grid.Column width={6} style={{marginTop: "0.3rem"}}>
              <i>{fastEntryResponseMsg}</i>
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderStockAdministrationSegment = (values: any) => {
    const {getMessage} = this.props;
    const {supplierOptions} = this.state;

    return (
        <Grid>
          <Grid.Row>
            <Grid.Column width={6} verticalAlign={'middle'}>
              {getMessage("stockAdministration.supplier")}
            </Grid.Column>
            <Grid.Column width={10}>
              <Field
                  name="supplierId"
                  component={Select}
                  options={supplierOptions}
                  clearable
                  fluid
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={6} verticalAlign={'middle'}>
              <label htmlFor="quantity">
                {`${getMessage("stockAdministration.quantity")} ${this.getQuantityDifference(values.quantity)}`}
              </label>
            </Grid.Column>
            <Grid.Column width={10}>
              <Field
                  fluid
                  id="quantity"
                  name="quantity"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={6} verticalAlign={'middle'}>
              <label htmlFor="minimum">{getMessage("stockAdministration.minimum")}</label>
            </Grid.Column>
            <Grid.Column width={10}>
              <Field
                  fluid
                  id="minimum"
                  name="minimum"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={6} verticalAlign={'middle'}>
              <label htmlFor="minimumQuantity">{getMessage("stockAdministration.minimumQuantity")}</label>
            </Grid.Column>
            <Grid.Column width={10}>
              <Field
                  fluid
                  id="minimumQuantity"
                  name="minimumQuantity"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={6} verticalAlign={'middle'}>
              <label htmlFor="storePlace">{getMessage("stockAdministration.storePlace")}</label>
            </Grid.Column>
            <Grid.Column width={10}>
              <Field
                  fluid
                  id="storePlace"
                  name="storePlace"
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
    )
  }

  renderMedicamentSegment = () => {
    const {getMessage} = this.props;
    const {medicamentData} = this.state;

    return (
        <Grid>
          <Grid.Row style={medTitleStyle}>
            <Grid.Column width={16}>
              <h3>{getMessage("stockAdministration.medicament.info")}</h3>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row style={medStyle}>
            <Grid.Column width={6}>
              <strong>{getMessage("stockAdministration.medicament.description")}</strong>
            </Grid.Column>
            <Grid.Column width={10}>
              <strong>{medicamentData ? medicamentData.description : ""}</strong>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row style={medStyle}>
            <Grid.Column width={6}>
              <strong>{getMessage("stockAdministration.quantity")}</strong>
            </Grid.Column>
            <Grid.Column width={10}>
              <strong>{this.getDbQuantity()}</strong>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row style={medStyle}>
            <Grid.Column width={6}>
              <strong>{getMessage("stockAdministration.medicament.unitQty")}</strong>
            </Grid.Column>
            <Grid.Column width={10}>
              <strong>{medicamentData ? medicamentData.unitQty : ""}</strong>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row style={medStyle}>
            <Grid.Column width={6}>
              <strong>{getMessage("stockAdministration.medicament.eanCode")}</strong>
            </Grid.Column>
            <Grid.Column width={10}>
              <strong>{medicamentData ? medicamentData.eanCode : ""}</strong>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row style={medStyle}>
            <Grid.Column width={6}>
              <strong>{getMessage("pharmaCode")}</strong>
            </Grid.Column>
            <Grid.Column width={10}>
              <strong>{medicamentData ? medicamentData.pharmaCode : ""}</strong>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row style={medStyle}>
            <Grid.Column width={6}>
              <strong>{getMessage("stockAdministration.medicament.billingPrice")}</strong>
            </Grid.Column>
            <Grid.Column width={10}>
              <strong>{medicamentData ? medicamentData.billingPrice : ""}</strong>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row style={medStyle}>
            <Grid.Column width={6}>
              <strong>{getMessage("stockAdministration.medicament.doctorsPrice")}</strong>
            </Grid.Column>
            <Grid.Column width={10}>
              <strong>{medicamentData ? medicamentData.doctorsPrice : ""}</strong>
            </Grid.Column>
          </Grid.Row>
        </Grid>
    )
  }
}

export default withMessages(UpsertStockAdministrationForm);
